import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import {
  type BasePuiDialogProps,
  type Nil,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { createBundle } from '~/store/actions/bundles'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import {
  getBundlesIsCreating,
  getLastCreatedBundleId,
} from '~/store/reducers/bundles'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Bundle as BundleType, BundleItem } from '~/types'
import {
  deleteItemByPrice,
  mergeItemsWithQuantitySum,
} from '~/utils/invoiceUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import Bundle, { type BundleHandle } from './Bundle'
import BundleItems, { type BundleItemsHandle } from './BundleItems'
import FinalizeBundle, { type FinalizeBundleHandle } from './FinalizeBundle'
import { useBundleUpdate } from './hooks/useBundleUpdate'

const useStyles = makeStyles(
  () => ({
    defaultPaper: {
      width: 650,
      maxWidth: 650,
    },
    nonDefaultPaper: {
      width: 976,
      maxWidth: 976,
    },
    finalizePaper: {
      width: 'calc(100% - 96px)',
      maxWidth: 1308,
    },
  }),
  { name: 'BundleDialog' },
)

export enum Steps {
  INITIAL = 'INITIAL',
  ITEMS_LIST = 'ITEMS_LIST',
  FINALIZE = 'FINALIZE',
}

const isEmptyBundle = (bundle: BundleType | Nil) =>
  Object.values(R.omit(['active'], bundle)).every(
    (value) => !value || R.isEmpty(value),
  )

interface BundleDialogProps extends BasePuiDialogProps {
  bundle: BundleType
  finalizeProceedButtonLabel?: string
  isClone?: boolean
  onBundleReady?: (bundle: BundleType) => void
  step: Steps
}

const BundleDialog = ({
  bundle,
  open,
  onBundleReady: onBundleReadyProp,
  step: stepProp,
  onClose,
  finalizeProceedButtonLabel,
  isClone = false,
}: BundleDialogProps) => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useDispatch()
  const lastCreatedBundleId = useSelector(getLastCreatedBundleId)
  const { t } = useTranslation(['Common', 'Dialogs'])

  const [step, setStep] = useState(stepProp || Steps.INITIAL)
  const isSetBundlePriceEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SET_BUNDLE_PRICE),
  )
  const isCurrentBusinessOmniChannel = useSelector(
    getCurrentBusinessIsOmniChannel,
  )
  const shouldUseSetBundlePrice =
    isSetBundlePriceEnabled && isCurrentBusinessOmniChannel

  const {
    bundleCandidate,
    setBundleCandidate,
    handleUpdateBundleCandidate: updateBundle,
    isDifferenceDistributionLoading,
    resetDifferenceDistribution,
  } = useBundleUpdate({
    shouldUseSetBundlePrice,
    initialBundle: bundle,
  })

  const handleClose = () => {
    setStep(stepProp || Steps.INITIAL)
    setBundleCandidate(bundle)
    if (onClose) {
      onClose()
    }
  }

  const onBundleCreated = () => {
    handleClose()
    if (lastCreatedBundleId) {
      navigate(`/admin/catalog/bundles/${lastCreatedBundleId}`)
    }
  }

  const setCloseAfterCreationOn = useCloseAfterCreation(
    onBundleCreated,
    getBundlesIsCreating,
  )

  useEffect(
    () => () => {
      resetDifferenceDistribution()
    },
    [],
  )

  useEffect(() => {
    setStep(stepProp || Steps.INITIAL)
  }, [stepProp])

  useEffect(() => {
    setBundleCandidate(bundle)
  }, [bundle])

  const onBundleCandidateReady = (newBundle: BundleType) => {
    setBundleCandidate(newBundle)
    setStep(Steps.ITEMS_LIST)
  }

  const handleUpdateBundleCandidate = (
    newBundle: BundleType,
    shouldFetchDifferenceDistribution: boolean = true,
    moveToFinalize: boolean = false,
  ) => {
    updateBundle(
      newBundle,
      shouldFetchDifferenceDistribution,
      moveToFinalize ? () => setStep(Steps.FINALIZE) : undefined,
    )
  }

  const onBundleItemsProceedRequested = (items: BundleItem[]) => {
    const newBundle = {
      ...bundleCandidate,
      items: bundleCandidate?.id
        ? mergeItemsWithQuantitySum(
            bundleCandidate?.items || [],
            items,
            R.path(['price', 'id']),
          )
        : items,
    }
    handleUpdateBundleCandidate(newBundle, true, true)
  }

  const onBundleItemsBackRequested = () => {
    if (stepProp) {
      handleClose()
    } else {
      setStep(Steps.INITIAL)
    }
  }

  const onFinalizeBundleBackRequested = () => {
    setStep(Steps.ITEMS_LIST)
  }

  const onFinalizeBundleProceedRequested = (newBundle: BundleType) => {
    if (onBundleReadyProp) {
      onBundleReadyProp(newBundle)
      handleClose()
    } else {
      setCloseAfterCreationOn()
      dispatch(createBundle(newBundle))
    }
  }

  const bundleRef = useRef<BundleHandle>(null)
  const bundleItemsRef = useRef<BundleItemsHandle>(null)
  const finalizeBundleRef = useRef<FinalizeBundleHandle>(null)

  const confirmCloseDialogProps =
    step !== Steps.FINALIZE
      ? {
          title: t('Dialogs:BUNDLE_DIALOG.CONFIRM_CLOSE_DIALOG_TITLE'),
          okLabel: t('Common:CONTINUE_ACTION'),
          notOkLabel: t('Common:EXIT_ACTION'),
        }
      : {
          onOk: () => {
            const candidate = finalizeBundleRef.current?.getCandidate()
            if (candidate) {
              onFinalizeBundleProceedRequested(candidate)
            }
          },
        }

  const hasChangesMap = {
    [Steps.INITIAL]: () => {
      const currentBundle = bundleRef.current?.getBundle()
      const hasUnsavedChanges = bundleRef.current?.hasUnsavedChanges()

      return Boolean(
        bundleCandidate ? hasUnsavedChanges : !isEmptyBundle(currentBundle),
      )
    },
    [Steps.ITEMS_LIST]: () => {
      const currentBundleItems = bundleItemsRef.current?.getItems()
      return Boolean(
        bundleCandidate && !bundle
          ? !isEmptyBundle(bundleCandidate)
          : currentBundleItems?.length && currentBundleItems.length > 0,
      )
    },
    [Steps.FINALIZE]: R.T,
  }

  return (
    <PuiDialog
      confirmSaveOnClose
      ConfirmCloseDialogProps={confirmCloseDialogProps}
      aria-labelledby="bundle-dialog"
      classes={{
        paper: classNames(classes.defaultPaper, {
          [classes.nonDefaultPaper]:
            step === Steps.ITEMS_LIST || step === Steps.FINALIZE,
          [classes.finalizePaper]: step === Steps.FINALIZE,
        }),
      }}
      hasUnsavedChanges={hasChangesMap[step]}
      open={open}
      title={
        <>
          {step === Steps.INITIAL && t('Dialogs:BUNDLE_DIALOG.TITLE_INITIAL')}
          {step === Steps.ITEMS_LIST &&
            t('Dialogs:BUNDLE_DIALOG.TITLE_ITEMS_LIST')}
          {step === Steps.FINALIZE && t('Dialogs:BUNDLE_DIALOG.TITLE_FINALIZE')}
        </>
      }
      onClose={handleClose}
    >
      {step === Steps.INITIAL && (
        <Bundle
          bundle={bundleCandidate}
          ref={bundleRef}
          onProceed={
            isClone
              ? (newBundle) => onFinalizeBundleProceedRequested(newBundle)
              : onBundleCandidateReady
          }
        />
      )}
      {step === Steps.ITEMS_LIST && (
        <BundleItems
          bundle={{
            ...bundleCandidate,
            items: bundleCandidate?.id
              ? deleteItemByPrice(bundle?.items, bundleCandidate?.items)
              : bundleCandidate.items,
          }}
          ref={bundleItemsRef}
          onBack={onBundleItemsBackRequested}
          onProceed={onBundleItemsProceedRequested}
        />
      )}
      {step === Steps.FINALIZE && (
        <FinalizeBundle
          bundle={bundleCandidate}
          isLoading={isDifferenceDistributionLoading}
          originalBundle={bundle}
          proceedButtonLabel={finalizeProceedButtonLabel}
          ref={finalizeBundleRef}
          onBack={onFinalizeBundleBackRequested}
          onProceed={onFinalizeBundleProceedRequested}
          onUpdateBundleCandidate={handleUpdateBundleCandidate}
        />
      )}
    </PuiDialog>
  )
}

export default BundleDialog
